<script setup lang="ts">
import { ApiPutMateMail } from "@/apis/interaction";
import { useInteractiveReview } from "@/stores/interactive-review";
import { InteractionEvent, MateMail } from "@/types/entity";
import MateMailEditPopup from "@/views/Mate/Mail/MateMailEditPopup.vue";
import { showLoadingToast } from "vant";
import { computed, nextTick, onMounted, onUnmounted, ref } from "vue";
import ThreadList, { ThreadListInstance } from "../mobile/ThreadList.vue";
import { useNotification } from "@/stores/notification";

const active = ref<0 | 1>(0);

const reviewStore = useInteractiveReview();
const unreviewListRef = ref<ThreadListInstance | null>(null);
const reviewedListRef = ref<ThreadListInstance | null>(null);
const notificationStore = useNotification();

const onReviewEvent = async () => {
  await nextTick();
  reviewedListRef.value?.resetScrollLoadingState();
};

onMounted(async () => {
  reviewStore.eventBus.on(onReviewEvent);
  await reviewStore.initOrFetchList(false, true);
  await nextTick();
  unreviewListRef.value?.resetScrollLoadingState();
});

onUnmounted(() => {
  reviewStore.eventBus.off(onReviewEvent);
});

const showMailEditPopup = ref(false);
const targetInteractionEvent = ref<InteractionEvent | null>(null);
const targetMateMail = computed<MateMail | null>(() => {
  return targetInteractionEvent.value?.entity as MateMail | null;
});

const startEditMail = async (interactionEvent: InteractionEvent) => {
  targetInteractionEvent.value = interactionEvent;
  await nextTick();
  showMailEditPopup.value = true;
};

const onSaveMail = async (mateMail: MateMail) => {
  const loading = showLoadingToast("");
  try {
    const old = reviewStore.entities[targetInteractionEvent.value!.id];
    old.entity = {
      ...old.entity,
      ...mateMail,
    };
    await ApiPutMateMail(old.entity.id, {
      subject: mateMail.subject,
      body: mateMail.body,
    });
  } finally {
    loading.close();
  }
};
</script>

<template>
  <div class="w-full h-full grid grid-rows-[auto_1fr] overflow-hidden">
    <ThreadsNav :show-center="false" show-back>
      <template #right>
        <CrystalPowerCount />
      </template>
    </ThreadsNav>
    <div class="w-full h-full overflow-hidden">
      <div class="w-full relative h-full overflow-hidden">
        <van-tabs
          v-model:active="active"
          shrink
          class="h-full grid grid-rows-[auto_1fr]"
          swipeable
        >
          <van-tab
            :title="$t('interactive.reviewPage.unreviewed')"
            :badge="
              notificationStore.interactionSummary
                ?.pendingInteractionReviewCount
            "
            :show-zero-badge="false"
          >
            <ThreadList
              ref="unreviewListRef"
              :immediate-check="false"
              :on-pull-load="
                async () => {
                  await reviewStore.initOrFetchList(false, true);
                  await nextTick();
                  unreviewListRef?.resetScrollLoadingState();
                }
              "
              :on-scroll-load="() => reviewStore.fetchListNextPage(false)"
              class="w-full"
            >
              <div class="p-4 flex flex-col gap-2">
                <InteractionItem
                  v-for="event in reviewStore.unreviewedList"
                  :key="event.id"
                  :event="event"
                  @edit-mail="startEditMail"
                  class="review-item"
                />
              </div>
            </ThreadList>
          </van-tab>
          <van-tab :title="$t('interactive.reviewPage.reviewed')">
            <ThreadList
              ref="reviewedListRef"
              :on-pull-load="
                async () => {
                  await reviewStore.initOrFetchList(true, true);
                  await nextTick();
                  reviewedListRef?.resetScrollLoadingState();
                }
              "
              :on-scroll-load="() => reviewStore.fetchListNextPage(true)"
              class="w-full"
            >
              <div class="p-4 flex flex-col gap-2">
                <InteractionItem
                  v-for="event in reviewStore.reviewedList"
                  :key="event.id"
                  :event="event"
                  :is-need-action="false"
                  class="review-item"
                />
              </div>
            </ThreadList>
          </van-tab>
        </van-tabs>
        <button v-if="false" class="btn btn-xs rounded absolute right-2 top-2">
          <i class="iconify fluent--settings-16-regular" />
          {{ $t("User.mate.setting") }}
        </button>
      </div>
    </div>
    <MateMailEditPopup
      v-if="targetMateMail"
      v-model:show="showMailEditPopup"
      :mate-mail="targetMateMail!"
      :enable-select-sender="false"
      :enable-select-recipient="false"
      @save="onSaveMail"
    />
  </div>
</template>

<style scoped>
.review-item {
  border-width: 1px;
  background-color: var(--b3);
  @apply border-base-content/10 rounded-lg;
}
</style>
